/**
 * WallboardZugang.jsx
 * Dialog fuer Super-Admins: Benutzername und Passwort fuer die Raumanzeige
 * setzen.
 *
 * Warum es diesen Dialog gibt: Die Alternative waere eine Umgebungsvariable
 * mit dem Hash gewesen — jede Passwortaenderung haette dann eine Aenderung am
 * Kubernetes-Secret und einen Pod-Neustart bedeutet. Hier dauert sie eine
 * halbe Minute, ohne Deploy.
 *
 * Das Passwort verlaesst dieses Formular nur einmal (an den Server, ueber
 * HTTPS) und wird dort ausschliesslich als scrypt-Hash abgelegt. Es wird
 * nirgends zurueckgeliefert und ist danach nicht mehr auslesbar — auch nicht
 * fuer Administratoren.
 */
import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function csrfHeaders(extra = {}) {
  const m = document.cookie.match(/(?:^|; )sorba_csrf=([^;]+)/);
  return m ? { ...extra, 'X-CSRF-Token': m[1] } : extra;
}

export default function WallboardZugang({ onClose }) {
  const [stand,    setStand]    = useState(null);
  const [benutzer, setBenutzer] = useState('wallboard');
  const [passwort, setPasswort] = useState('');
  const [wdh,      setWdh]      = useState('');
  const [fehler,   setFehler]   = useState(null);
  const [fertig,   setFertig]   = useState(null);
  const [laeuft,   setLaeuft]   = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/wallboard-passwort`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setStand(d); if (d.benutzer) setBenutzer(d.benutzer); })
      .catch(() => setStand({ eingerichtet: false }));
  }, []);

  const speichern = async (e) => {
    e.preventDefault();
    setFehler(null);
    if (passwort !== wdh)      return setFehler('Die beiden Passwörter stimmen nicht überein.');
    if (passwort.length < 12)  return setFehler('Mindestens 12 Zeichen.');
    setLaeuft(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/wallboard-passwort`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ benutzer, passwort }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(d.error || 'Speichern fehlgeschlagen'); return; }
      setFertig(d);
      setPasswort(''); setWdh('');
    } catch {
      setFehler('Keine Verbindung zum Server');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.dialog} onClick={e => e.stopPropagation()}>
        <div style={S.kopf}>
          <h2 style={S.titel}>Wallboard-Zugang</h2>
          <button style={S.schliessen} onClick={onClose} title="Schliessen">✕</button>
        </div>

        <p style={S.text}>
          Zugang für den Bildschirm im Raum, erreichbar unter <code>/wallboard</code>.
          Er gibt ausschliesslich die Warteschlangen-Anzeige frei — nicht die
          Ticket-Analyse und nicht die Mitarbeiter-Ansicht.
        </p>

        {stand && (
          <div style={S.stand}>
            {stand.eingerichtet
              ? <>Eingerichtet als <strong>{stand.benutzer}</strong>
                  {stand.gesetztAm && <> · zuletzt geändert {new Date(stand.gesetztAm).toLocaleString('de-CH')}</>}
                  {stand.gesetztVon && <> von {stand.gesetztVon}</>}
                  {stand.sessionTage && <> · Anmeldung gilt {stand.sessionTage} Tage</>}
                </>
              : 'Noch kein Zugang eingerichtet.'}
          </div>
        )}

        {fertig ? (
          <div style={S.erfolg}>
            Gespeichert. Der Bildschirm muss sich unter <code>/wallboard</code> einmal
            neu anmelden — alle bisherigen Wallboard-Anmeldungen sind jetzt ungültig.
          </div>
        ) : (
          <form onSubmit={speichern} style={S.form}>
            <label style={S.label}>
              Benutzername
              <input style={S.input} value={benutzer} onChange={e => setBenutzer(e.target.value)} />
            </label>
            <label style={S.label}>
              Passwort <span style={S.klein}>(mindestens 12 Zeichen)</span>
              <input style={S.input} type="password" value={passwort}
                onChange={e => setPasswort(e.target.value)} autoComplete="new-password" />
            </label>
            <label style={S.label}>
              Passwort wiederholen
              <input style={S.input} type="password" value={wdh}
                onChange={e => setWdh(e.target.value)} autoComplete="new-password" />
            </label>
            {fehler && <div style={S.fehler}>{fehler}</div>}
            <button style={S.knopf} type="submit" disabled={laeuft || !passwort || !wdh}>
              {laeuft ? 'Speichern…' : stand?.eingerichtet ? 'Passwort ändern' : 'Zugang einrichten'}
            </button>
            <p style={S.hinweis}>
              Das Passwort wird nur als Hash gespeichert und ist danach nicht mehr
              auslesbar — auch nicht hier. Notiere es dir, bevor du speicherst.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dialog:   { width: 'min(460px, 92vw)', background: 'var(--bg-card)',
              color: 'var(--text-primary)', borderRadius: 10, padding: '18px 20px 20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)' },
  kopf:     { display: 'flex', alignItems: 'center', marginBottom: 6 },
  titel:    { fontSize: 16, fontWeight: 600, margin: 0 },
  schliessen: { marginLeft: 'auto', border: 'none', background: 'transparent',
                fontSize: 15, cursor: 'pointer', color: 'var(--text-secondary)' },
  text:     { fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '4px 0 12px' },
  stand:    { fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-secondary, #f6f8fa)',
              padding: '8px 10px', borderRadius: 6, marginBottom: 14 },
  form:     { display: 'flex', flexDirection: 'column', gap: 12 },
  label:    { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
              color: 'var(--text-secondary)' },
  klein:    { fontWeight: 400, opacity: 0.8 },
  input:    { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border, #d1d5db)',
              background: 'var(--bg-input, #fff)', color: 'var(--text-primary)', fontSize: 14 },
  knopf:    { padding: '9px 14px', borderRadius: 6, border: 'none', background: '#185FA5',
              color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  // Fest eingetragene Farben taugen nur fuer EINEN Modus — im Dunkelmodus war
  // das dunkle Gruen auf hellgruenem Grund (05.10.2026 aufgefallen).
  fehler:   { fontSize: 13, color: 'var(--color-error)' },
  erfolg:   { fontSize: 13, lineHeight: 1.55, color: 'var(--color-success)',
              background: 'var(--bg-secondary)', padding: '10px 12px', borderRadius: 6 },
  hinweis:  { fontSize: 11, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 },
};
